import { useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initial = { postcode: "", acreage: "", intended_use: "Residential", name: "", email: "", phone: "", notes: "" };

export default function Appraisal() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.postcode.trim() || !form.acreage.trim() || !form.name.trim() || !form.email.trim()) {
      toast.error("Please complete postcode, acreage, name and email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/appraisals`, form);
      setReport(data.report);
      toast.success("Your appraisal is ready below.");
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="appraisal-page" className="bg-[#0B0F0E]">
      <Toaster position="top-center" richColors theme="dark" />
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-12">
        <div className="overline"><span className="h-line" />Free Initial Appraisal</div>
        <h1 className="font-display text-[#F4EFE6] text-6xl md:text-7xl lg:text-[5.5rem] mt-7 leading-[0.95] max-w-4xl">
          What is <span className="italic-gold">your land</span> worth?
        </h1>
        <p className="text-[#9BA39B] text-lg max-w-3xl mt-9 leading-relaxed">
          Tell us your postcode and acreage. We&apos;ll generate an instant first-look planning context for your site and follow up with a more detailed view from a real person within two working days.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pb-28 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <form data-testid="appraisal-form" onSubmit={submit} className="bg-[#11171A] border border-[#F4EFE6]/10 p-8 md:p-12 space-y-7">
            <div className="grid md:grid-cols-2 gap-7">
              <div>
                <label className="overline block mb-2">Site postcode *</label>
                <input data-testid="appraisal-postcode" className="underline-input uppercase tracking-[0.15em]" value={form.postcode} onChange={onChange("postcode")} placeholder="e.g. GL54 3DR" />
              </div>
              <div>
                <label className="overline block mb-2">Approx. acreage *</label>
                <input data-testid="appraisal-acreage" className="underline-input" value={form.acreage} onChange={onChange("acreage")} placeholder="e.g. 12 acres" />
              </div>
            </div>

            <div>
              <label className="overline block mb-2">Intended use</label>
              <select data-testid="appraisal-use" className="underline-input" value={form.intended_use} onChange={onChange("intended_use")}>
                <option>Residential</option>
                <option>Commercial</option>
                <option>Mixed-Use</option>
                <option>Brownfield</option>
              </select>
            </div>

            <div className="border-t border-[#F4EFE6]/10 pt-7 grid md:grid-cols-2 gap-7">
              <div>
                <label className="overline block mb-2">Your name *</label>
                <input data-testid="appraisal-name" className="underline-input" value={form.name} onChange={onChange("name")} />
              </div>
              <div>
                <label className="overline block mb-2">Email *</label>
                <input data-testid="appraisal-email" type="email" className="underline-input" value={form.email} onChange={onChange("email")} />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Phone (optional)</label>
                <input data-testid="appraisal-phone" className="underline-input" value={form.phone} onChange={onChange("phone")} />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Anything else?</label>
                <textarea data-testid="appraisal-notes" rows={3} className="underline-input resize-none" value={form.notes} onChange={onChange("notes")} />
              </div>
            </div>

            <button data-testid="appraisal-submit" type="submit" disabled={loading} className="usl-btn">
              {loading ? "Generating..." : "Generate My Appraisal"} <Sparkles size={16} />
            </button>
            <p className="text-xs text-[#9BA39B]">Confidential. We use your details only to respond. We never sell or share your data.</p>
          </form>
        </div>

        <aside className="lg:col-span-5">
          {report ? (
            <div data-testid="appraisal-report" className="bg-[#C9A24B] text-[#0B0F0E] p-8 md:p-10">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={22} />
                <div className="text-[10px] tracking-[0.28em] uppercase font-semibold">Your Initial Appraisal</div>
              </div>
              <pre className="font-body whitespace-pre-wrap text-[14px] leading-relaxed mt-6">{report}</pre>
              <div className="flex flex-wrap gap-4 mt-8">
                <a data-testid="appraisal-cta-sell" href="/sell-your-land" className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold link-underline">
                  Submit a Full Site Brief <ArrowUpRight size={14} />
                </a>
                <a data-testid="appraisal-cta-call" href="/book-a-call" className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold border border-[#0B0F0E] px-5 py-2.5 hover:bg-[#0B0F0E] hover:text-[#C9A24B] transition-colors">
                  Book a Call Instead <Sparkles size={14} />
                </a>
              </div>
            </div>
          ) : (
            <div className="border border-[#F4EFE6]/10 bg-[#11171A] p-8 md:p-10">
              <div className="overline">What you&apos;ll receive</div>
              <ul className="mt-6 space-y-4 text-[15px] text-[#E8E1D2] leading-relaxed">
                <li className="flex gap-3"><span className="text-[#C9A24B]">—</span><span>An instant snapshot of your land&apos;s planning context, on screen.</span></li>
                <li className="flex gap-3"><span className="text-[#C9A24B]">—</span><span>A follow-up from a real planner within two working days.</span></li>
                <li className="flex gap-3"><span className="text-[#C9A24B]">—</span><span>A confidential conversation — no obligation, no cost.</span></li>
                <li className="flex gap-3"><span className="text-[#C9A24B]">—</span><span>If we proceed: promotion entirely at our cost.</span></li>
              </ul>
            </div>
          )}
        </aside>
      </section>
    </div>
  );
}
